/**
 * useConversations
 *
 * Tracks every DM thread the current user has participated in so chat
 * threads stay openable even when the peer is offline or no longer
 * returned by useUsers().
 *
 * Persistence strategy:
 *  - Primary:  localStorage  (key: `joystance_convos_<myId>`)
 *  - Backup:   GUN           (path: joystance_convos/<myId>/<peerId>)
 *
 * The GUN copy is written whenever a conversation is recorded, so the
 * list survives recovery-code logins on new devices.
 * Unread counts are per-device (localStorage only) — that matches how
 * every other messaging app handles unread badges.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import gun from '../config/gun.config';
import { AvatarConfig } from '../types/user';

export interface StoredConversation {
  peerId: string;
  peerNick: string;
  peerAvatar?: AvatarConfig;
  lastMessagePreview: string; // plain text or "🔒 Encrypted message"
  lastMessageAt: number;
  unread: number;
}

function lsKey(myId: string) {
  return `joystance_convos_${myId}`;
}

// ── Persisted "seen notification IDs" ────────────────────────────────────────
// GUN replays ALL stored notifications on every page load / reconnect.
// Without persistence, notifSeen starts empty each session and every historical
// notification fires again, inflating unread counts to hundreds.
const SEEN_KEY = (id: string) => `joystance_notif_seen_${id}`;
const MAX_SEEN = 2000; // cap to avoid unbounded localStorage growth

function loadSeenIds(myId: string): Set<string> {
  try {
    const raw = localStorage.getItem(SEEN_KEY(myId));
    return raw ? new Set(JSON.parse(raw) as string[]) : new Set();
  } catch { return new Set(); }
}

function saveSeenIds(myId: string, ids: Set<string>) {
  try {
    // Keep only the most-recent MAX_SEEN entries
    const arr = Array.from(ids);
    localStorage.setItem(SEEN_KEY(myId), JSON.stringify(arr.slice(-MAX_SEEN)));
  } catch { /* storage full — best effort */ }
}

function loadFromLS(myId: string): StoredConversation[] {
  try {
    const raw = localStorage.getItem(lsKey(myId));
    if (!raw) return [];
    return JSON.parse(raw);
  } catch { return []; }
}

function saveToLS(myId: string, convos: StoredConversation[]) {
  try {
    localStorage.setItem(lsKey(myId), JSON.stringify(convos));
  } catch { /* storage full – best effort */ }
}

function parseAvatar(raw: any): AvatarConfig | undefined {
  if (!raw) return undefined;
  if (typeof raw === 'object' && raw.emoji) return raw as AvatarConfig;
  try { return JSON.parse(raw); } catch { return undefined; }
}

export function useConversations(myId: string | null) {
  const [convos, setConvos] = useState<StoredConversation[]>([]);
  const gunSyncedPeers = useRef(new Set<string>());

  // ── Load persisted data on mount ──────────────────────────────────────────
  useEffect(() => {
    if (!myId) return;

    // 1. Restore from localStorage immediately (no flicker)
    const local = loadFromLS(myId);
    if (local.length > 0) setConvos(local);

    // 2. Merge from GUN (cross-device recovery)
    const node = gun.get('joystance_convos').get(myId);
    node.map().once((data: any, peerId: string) => {
      if (!data || !peerId || peerId === '_') return;
      setConvos(prev => {
        const exists = prev.find(c => c.peerId === peerId);
        if (exists) {
          // Only update if GUN entry is newer
          if (data.lastMessageAt && data.lastMessageAt > exists.lastMessageAt) {
            const updated = prev.map(c =>
              c.peerId === peerId
                ? {
                    ...c,
                    peerNick: data.peerNick || c.peerNick,
                    peerAvatar: parseAvatar(data.peerAvatar) || c.peerAvatar,
                    lastMessagePreview: data.lastMessagePreview || c.lastMessagePreview,
                    lastMessageAt: data.lastMessageAt,
                    // Don't overwrite per-device unread from GUN
                  }
                : c
            ).sort((a, b) => b.lastMessageAt - a.lastMessageAt);
            saveToLS(myId, updated);
            return updated;
          }
          return prev;
        }
        // New conversation from another device
        const newConvo: StoredConversation = {
          peerId,
          peerNick: data.peerNick || peerId.slice(-6),
          peerAvatar: parseAvatar(data.peerAvatar),
          lastMessagePreview: data.lastMessagePreview || '🔒 Encrypted message',
          lastMessageAt: data.lastMessageAt || Date.now(),
          unread: 0, // Don't inherit unread from other devices
        };
        const next = [...prev, newConvo].sort((a, b) => b.lastMessageAt - a.lastMessageAt);
        saveToLS(myId, next);
        return next;
      });
    });
  }, [myId]);

  // ── Listen for incoming DM notifications ──────────────────────────────────
  // This adds the sender to the conversation list even if you never
  // opened a chat with them before.
  //
  // IMPORTANT: notifSeen is loaded from localStorage on each myId change so
  // that GUN's replay of all historical notifications (which fires on every
  // page load / reconnect) does not re-increment unread counts for old messages.
  const notifSeen = useRef(new Set<string>());
  useEffect(() => {
    if (!myId) return;

    // Restore previously-seen IDs so replayed GUN events are de-duped
    notifSeen.current = loadSeenIds(myId);

    const node = gun.get('joystance_notifications').get(myId);
    const mapNode = node.map();
    mapNode.on((data: any, id: string) => {
      if (!data || !id || notifSeen.current.has(id)) return;
      if (data.type !== 'dm' && data.type !== 'global_reply') return;
      notifSeen.current.add(id);
      saveSeenIds(myId, notifSeen.current);

      const ts: number = data.ts || Date.now();

      if (data.type === 'global_reply') {
        // Surface as the Global Room pseudo-conversation so the badge increments
        const snippet = data.replyText
          ? `↩ ${data.fromNick}: ${data.replyText}`
          : `↩ ${data.fromNick} replied to you`;
        upsertConvo('global', 'Global Room', undefined, snippet, ts, true);
        return;
      }

      const peerId: string = data.from;
      const peerNick: string = data.fromNick || peerId?.slice(-6) || 'Unknown';
      const preview = '🔒 Encrypted message';
      upsertConvo(peerId, peerNick, undefined, preview, ts, true /* increment unread */);
    });

    return () => { try { mapNode.off(); } catch {} };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [myId]);

  // ── Core upsert helper ────────────────────────────────────────────────────
  const upsertConvo = useCallback((
    peerId: string,
    peerNick: string,
    peerAvatar: AvatarConfig | undefined,
    preview: string,
    ts: number,
    addUnread: boolean,
  ) => {
    if (!myId || !peerId) return;

    setConvos(prev => {
      const idx = prev.findIndex(c => c.peerId === peerId);
      let next: StoredConversation[];

      if (idx >= 0) {
        next = [...prev];
        next[idx] = {
          ...next[idx],
          peerNick: peerNick || next[idx].peerNick,
          peerAvatar: peerAvatar || next[idx].peerAvatar,
          lastMessagePreview: preview || next[idx].lastMessagePreview,
          lastMessageAt: ts,
          unread: addUnread ? next[idx].unread + 1 : next[idx].unread,
        };
      } else {
        next = [
          ...prev,
          { peerId, peerNick, peerAvatar, lastMessagePreview: preview, lastMessageAt: ts, unread: addUnread ? 1 : 0 },
        ];
      }

      next = next.sort((a, b) => b.lastMessageAt - a.lastMessageAt);
      saveToLS(myId, next);

      // Sync metadata (no unread) to GUN for cross-device visibility
      if (!gunSyncedPeers.current.has(peerId) || addUnread) {
        gunSyncedPeers.current.add(peerId);
        gun.get('joystance_convos').get(myId).get(peerId).put({
          peerNick,
          peerAvatar: peerAvatar ? JSON.stringify(peerAvatar) : null,
          lastMessagePreview: preview,
          lastMessageAt: ts,
        } as any);
      }

      return next;
    });
  }, [myId]);

  // ── Public API ────────────────────────────────────────────────────────────

  /**
   * Call when you open a DM (send or receive).
   * Records the conversation so it persists in the list.
   */
  const record = useCallback((
    peerId: string,
    peerNick: string,
    peerAvatar?: AvatarConfig,
    preview = '🔒 Encrypted message',
    ts = Date.now(),
  ) => {
    upsertConvo(peerId, peerNick, peerAvatar, preview, ts, false);
  }, [upsertConvo]);

  /**
   * Call when the user opens a conversation to clear the unread badge.
   */
  const markRead = useCallback((peerId: string) => {
    if (!myId) return;
    setConvos(prev => {
      const next = prev.map(c =>
        c.peerId === peerId ? { ...c, unread: 0 } : c
      );
      saveToLS(myId, next);
      return next;
    });
  }, [myId]);

  /**
   * Mark ALL conversations as read (called when opening the chat tab).
   */
  const markAllRead = useCallback(() => {
    if (!myId) return;
    setConvos(prev => {
      if (prev.every(c => c.unread === 0)) return prev; // no-op if already clean
      const next = prev.map(c => ({ ...c, unread: 0 }));
      saveToLS(myId, next);
      return next;
    });
  }, [myId]);

  /**
   * Update the last message preview for a conversation after a successful
   * send (so sent messages also update the preview, not just received ones).
   */
  const updatePreview = useCallback((peerId: string, preview: string, ts: number) => {
    if (!myId) return;
    setConvos(prev => {
      const next = prev.map(c =>
        c.peerId === peerId
          ? { ...c, lastMessagePreview: preview, lastMessageAt: ts }
          : c
      ).sort((a, b) => b.lastMessageAt - a.lastMessageAt);
      saveToLS(myId, next);
      return next;
    });
    // Update GUN too
    gun.get('joystance_convos').get(myId).get(peerId).put({
      lastMessagePreview: preview,
      lastMessageAt: ts,
    } as any);
  }, [myId]);

  return { convos, record, markRead, markAllRead, updatePreview };
}
